import { useEffect, useEffectEvent, useId, useRef, useState, type KeyboardEvent } from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { useIsMobile } from "../hooks/useIsMobile";
import { useSiteConfig } from "../config/SiteConfigContext";
import { useCmsText } from "../hooks/useCmsText";
import { ErrorBoundary } from "../components/ErrorBoundary";
import { useRequestComposer } from "../richiesta/useRequestComposer";
import { RequestComposer } from "../richiesta/RequestComposer";
import { StepDashes } from "../richiesta/parts/StepIndicator";
import { Glyph } from "../richiesta/parts/Icons";
import { contextFromPath, isContactPath, parseOpenDetail, type OpenContext } from "../richiesta/context";
import { stepLabel } from "../richiesta/labels";
import { rootStyle } from "../richiesta/tokens";

gsap.registerPlugin(useGSAP);

/**
 * ContactWidget — «Inviaci una richiesta» in un pannello laterale, su ogni pagina.
 *
 * - Si apre dal pulsante tondo (FAB) o dall'evento window `gb:open-contact`
 *   (box «confezione personalizzata» del carrello). Un `detail` facoltativo
 *   porta le confezioni del carrello; senza `detail` si apre come dal FAB.
 * - È un dialog modale: titolo, focus dentro, Tab confinato, Esc chiude, il focus
 *   torna a chi l'ha aperto.
 * - Cambiando pagina si chiude (stato legato al percorso, nessun effetto).
 * - La richiesta (stesso invio di sempre: source send_request_aside) vive qui,
 *   in memoria: chiudere e riaprire non perde nulla.
 *
 * NB: la chat AI è conservata (commentata) in ChatWidget.tsx — vedi LocaleLayout.tsx.
 */

const FOCUSABLE = 'a[href], button, input, textarea, select, [tabindex]:not([tabindex="-1"])';

const motionOK = () => {
  try {
    return window.matchMedia("(prefers-reduced-motion: no-preference)").matches;
  } catch {
    return false;
  }
};

export function ContactWidget() {
  const { i18n } = useTranslation();
  const isMobile = useIsMobile();
  const location = useLocation();
  const cfg = useSiteConfig();
  const ct = useCmsText("contact");
  const titleId = useId();

  const api = useRequestComposer({
    source: "send_request_aside",
    locale: i18n.language || "it",
    gaLocation: "widget",
    surface: "panel",
  });

  // Aperto «su questa pagina»: cambiando percorso il pannello si chiude da solo.
  const [openPath, setOpenPath] = useState<string | null>(null);
  if (openPath !== null && openPath !== location.pathname) setOpenPath(null);
  const open = openPath !== null;
  // Montato durante l'uscita animata; smontato alla fine.
  const [visible, setVisible] = useState(false);
  if (open && !visible) setVisible(true);
  // Ogni apertura azzera l'ErrorBoundary interno: un errore passato non resta per sempre.
  const [openCount, setOpenCount] = useState(0);

  const panelRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const fabRef = useRef<HTMLButtonElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const userCloseRef = useRef(false);
  const onContactPage = isContactPath(location.pathname);

  function openWith(ctx: OpenContext | null) {
    const active = document.activeElement;
    openerRef.current = active instanceof HTMLElement && active !== document.body ? active : null;
    userCloseRef.current = false;
    api.applyContext(ctx);
    setOpenCount((n) => n + 1);
    setOpenPath(location.pathname);
  }

  const onOpenEvent = useEffectEvent((e: Event) => {
    const detail = parseOpenDetail((e as CustomEvent<unknown>).detail);
    openWith(detail ?? contextFromPath(location.pathname));
  });

  useEffect(() => {
    const handler = (e: Event) => onOpenEvent(e);
    window.addEventListener("gb:open-contact", handler);
    return () => window.removeEventListener("gb:open-contact", handler);
  }, []);

  function close() {
    userCloseRef.current = true;
    setOpenPath(null);
  }

  /** Dove torna il focus: chi ha aperto il pannello, il FAB, il carrello, il titolo del modulo della pagina. */
  function restoreFocus() {
    const opener = openerRef.current;
    openerRef.current = null;
    const candidates: Array<HTMLElement | null | undefined> = [
      opener && opener.isConnected && !opener.closest("[hidden]") ? opener : null,
      fabRef.current && !fabRef.current.hidden ? fabRef.current : null,
      document.querySelector<HTMLElement>('[data-gb="cart"]'),
      document.querySelector<HTMLElement>('[data-gb="contact-title"]'),
    ];
    candidates.find(Boolean)?.focus();
  }

  // Entrata e uscita del pannello; il focus entra all'apertura.
  useGSAP(() => {
    if (!visible) return;
    const panel = panelRef.current;
    const backdrop = backdropRef.current;
    if (open) {
      api.focusCurrent();
      if (!motionOK()) return;
      if (backdrop) gsap.fromTo(backdrop, { opacity: 0 }, { opacity: 1, duration: 0.3, ease: "power2.out" });
      if (panel) gsap.fromTo(panel, { x: "100%" }, { x: "0%", duration: 0.4, ease: "expo.out" });
      return;
    }
    const finish = () => {
      flushSync(() => {
        setVisible(false);
        // Chiuso dopo l'invio: l'istantanea con nome, email e messaggio non resta in memoria.
        if (api.state.phase === "success") api.dispatch({ type: "dismissSuccess" });
      });
      if (userCloseRef.current) restoreFocus();
      userCloseRef.current = false;
    };
    if (!motionOK()) {
      gsap.delayedCall(0, finish);
      return;
    }
    if (backdrop) gsap.to(backdrop, { opacity: 0, duration: 0.3, ease: "power2.inOut" });
    if (panel) gsap.to(panel, { x: "100%", duration: 0.3, ease: "power2.inOut", onComplete: finish });
    else gsap.delayedCall(0, finish);
    // `api` cambia a ogni render: qui servono solo le transizioni di apertura e chiusura.
    // revertOnUpdate: a ogni passaggio il contesto GSAP precedente si pulisce.
  }, { dependencies: [open, visible], revertOnUpdate: true });

  // Il pulsante tondo compare una volta, senza rimbalzi.
  useGSAP(() => {
    if (fabRef.current && motionOK()) gsap.from(fabRef.current, { scale: 0.6, opacity: 0, duration: 0.4, ease: "expo.out", clearProps: "transform,opacity" });
  }, []);

  function onPanelKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Escape") {
      e.stopPropagation();
      close();
      return;
    }
    if (e.key !== "Tab") return;
    const panel = panelRef.current;
    if (!panel) return;
    const items = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => !el.closest("[hidden]") && el.tabIndex >= 0);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  const { t, state, stepNumber, steps } = api;
  const subtitle = state.phase === "success" ? "" : t("contact.guide.panelSubtitle", { n: stepNumber, total: steps.length, name: stepLabel(t, state.step) });
  const contactEmail = cfg.contact_email || "info@granborgo.com";

  return (
    <>
      {/* Su /contatti il modulo è già la pagina: niente pulsante tondo (l'evento dal carrello apre comunque il pannello). */}
      <button
        ref={fabRef}
        type="button"
        hidden={visible || onContactPage}
        onClick={() => openWith(contextFromPath(location.pathname))}
        onPointerEnter={api.prefetch}
        aria-label={ct("formTitle")}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="rq-fab"
        data-gb="contact-fab"
      >
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4 4h16a1 1 0 011 1v14a1 1 0 01-1 1H4a1 1 0 01-1-1V5a1 1 0 011-1z" />
          <path d="M3 6l9 7 9-7" />
        </svg>
      </button>

      {visible && (
        <>
          {!isMobile && <div ref={backdropRef} className="rq-backdrop" onClick={close} aria-hidden="true" />}
          <div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className="rq-panel"
            style={rootStyle(cfg)}
            onKeyDown={onPanelKeyDown}
            // Focalizzabile da codice: un clic su un'area vuota lascia il focus nel
            // dialog (e quindi Esc e il Tab confinato funzionano ancora).
            tabIndex={-1}
            data-gb="pannello"
          >
            <div className="rq-panel__head">
              <span className="rq-panel__mono" aria-hidden="true">GB</span>
              <div className="rq-panel__titles">
                <h2 id={titleId} className="rq-panel__title">{ct("formTitle")}</h2>
                {subtitle && <p className="rq-panel__sub">{subtitle}</p>}
                {state.phase !== "success" && <StepDashes api={api} />}
              </div>
              <button type="button" className="rq-panel__close" onClick={close} aria-label={t("chat.close")} data-gb="chiudi">
                <Glyph name="close" size={14} />
              </button>
            </div>
            <ErrorBoundary
              resetKey={String(openCount)}
              fallback={
                <div className="rq-body" role="alert">
                  <p className="rq-alert">
                    {t("contact.guide.widgetFallback")}{" "}
                    <a href={`mailto:${contactEmail}`}>{contactEmail}</a>.
                  </p>
                </div>
              }
            >
              <RequestComposer api={api} variant="panel" titleId={titleId} />
            </ErrorBoundary>
          </div>
        </>
      )}
    </>
  );
}
